<template>
  <div>
    <!-- heading row -->
    <v-row class="mx-2 mt-1">
      <v-col cols="12" >
        <div class="text-right" v-if="currentUser.privileges.canAddFinanceIncomeTransactions">
          <v-btn color="primary" class="mb-3" @click="openAddDialog()">
            Add Income
          </v-btn>
        </div>
      </v-col>
      <v-spacer></v-spacer>
    </v-row>

    <v-row>
      <v-col>
        <v-card class="mx-2 mb-2">
          <v-row class="mx-2">
            <v-col cols="12" md="6">
              <h2 class="text-left mx-2">Income Transactions</h2>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="search"
                class="mb-1 mx-2 py-0"
                append-icon="mdi-magnify"
                label="Search"
                single-line
                hide-details
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="2" align-self="center" v-if="currentUser.privileges.canExportFinanceIncomeTransactions">
              <download-excel
                :fetch="getExportData"
                :fields="getExportFields"
                :name="currentUser.full_name + '_Income_transaction_records'"
              >
                <v-btn
                  class="text-right mr-1"
                  :loading="export_in_progress"
                  :disabled="export_in_progress"
                  color="white"
                  elevation="1"
                  block
                >
                  Export
                  <v-icon right dark> mdi-export </v-icon>
                  <template v-slot:loader>
                    <span class="custom-loader">
                      <v-icon light>mdi-cached</v-icon>
                    </span>
                  </template>
                </v-btn>
              </download-excel>
            </v-col>
          </v-row>

          <v-row no-gutters class="py-3 px-3">
            <!-- Filter by date range dropdown -->
            <v-col cols="12" md="3">
              <!-- Date-range picker -->
              <v-menu
                ref="menu"
                v-model="menu"
                :close-on-content-click="false"
                :return-value.sync="dateRange"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="formattedDateRange(dateRange)"
                    label="Filter by Date Range"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    hide-details
                    class="mx-2 mb-2 rounded-input"
                    outlined
                    dense
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="dateRange"
                  no-title
                  scrollable
                  range
                  @change="getTransactions()"
                >
                  <v-spacer></v-spacer>
                  <v-btn text color="primary" @click="menu = false">
                    Cancel
                  </v-btn>
                  <v-btn
                    text
                    color="primary"
                    @click="$refs.menu.save(dateRange)"
                  >
                    OK
                  </v-btn>
                </v-date-picker>
              </v-menu>
            </v-col>
            <!-- Filter by category dropdown -->
            <v-col cols="12" md="3">
              <v-autocomplete
                v-model="filter.category"
                :items="transactionCategoryItems"
                small-chips
                outlined
                color="primary"
                label="Filter by Category"
                hide-details
                dense
                item-value="id"
                background-color="transparent"
                :item-text="
                  (item) =>
                    item.title +
                    (item.transaction_type == 'E' ? ' (Expense)' : ' (Income)')
                "
                class="mx-2 mb-2 rounded-input"
                multiple
                @change="getTransactions()"
              >
              </v-autocomplete>
            </v-col>
            <!-- Filter by Payment mode dropdown -->
            <v-col cols="12" md="3">
              <v-autocomplete
                v-model="filter.mode"
                :items="paymentModes"
                small-chips
                outlined
                color="primary"
                label="Filter by Mode"
                hide-details
                dense
                background-color="transparent"
                item-text="title"
                item-value="type"
                class="mx-2 mb-2 rounded-input"
                multiple
                @change="getTransactions()"
              ></v-autocomplete>
            </v-col>

            <!-- Filter by Manager dropdown -->
            <v-col cols="12" md="3">
              <v-autocomplete
                v-model="filter.manager"
                :items="filterManagerItems"
                small-chips
                outlined
                color="primary"
                label="Filter by Manager"
                hide-details
                dense
                background-color="transparent"
                item-value="id"
                item-text="full_name"
                class="mx-2 mb-2 rounded-input"
                multiple
                @change="getTransactions()"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" md="3">
              <v-autocomplete
                v-model="filter.accounts"
                :items="allFinanceAccounts"
                small-chips
                outlined
                color="primary"
                label="Filter by Account"
                hide-details
                dense
                background-color="transparent"
                item-value="id"
                item-text="name"
                class="mb-2 mt-2 mx-2 rounded-input"
                multiple
                @change="getTransactions()"
              ></v-autocomplete>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-data-table
                :headers="transactionHeaders"
                :items="transactionItems"
                :search="search"
                :options.sync="options"
                :multi-sort="true"
                :items-per-page="15"
                :server-items-length="pagination.totalCount"
                @click:row="handleClick"
                class="mx-2 row-pointer"
              >
                <!-- Description tooltip -->
                <template v-slot:item.description="{ item }">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on">
                        {{
                          item.description.length > 26 ? item.description.substring(0, Math.min(item.description.length, 25)) + " ...": item.description
                        }}
                      </span>
                    </template>
                    <span>{{ item.description }}</span>
                  </v-tooltip>
                </template>

                 <template v-slot:item.amount="{ item }">
                  <span
                    :class="item.transaction_type == 'E' ? 'red--text' : 'green--text'"
                    ><strong>{{ "₹" + item.amount }}</strong></span
                  >
                </template>


                <!-- Date formatting -->
                <template v-slot:item.transaction_date="{ item }">
                  <div>
                    {{ moment(item.transaction_date).format("MMMM Do YYYY") }}
                  </div>
                </template>

                <!-- Invoice handling -->
                <template v-slot:item.receipt_number="{ item }">
                  <div v-if="item.receipt_number">
                    {{ item.receipt_number }}
                  </div>
                  <div v-else>
                    <h3>-</h3>
                  </div>
                </template>
              </v-data-table>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>

    <v-row justify="center">
      <v-dialog v-model="dialog" persistent max-width="650px">
        <v-card>
          <v-card-title class="justify-center">
            <span> {{ is_adding ? "Add" : "Edit" }} Transaction</span>
            <v-btn icon absolute top right color="dark" @click="closeDialog">
              x
            </v-btn>
          </v-card-title>
          <v-card-text class="">
            <v-form
              ref="form"
              :readonly="!is_adding && !is_editing"
              v-model="submitForm"
            >
              <v-container class="mb-3">
                <v-row>
                  <v-col cols="12" sm="6" md="6">
                    <v-select
                      label="Transaction Type*"
                      v-model="dialogValues.transaction_type"
                      prepend-icon="mdi-cash-multiple"
                      item-value="transaction_type"
                      :items="transactionTypeItems"
                      item-text="title"
                      readonly
                      :rules="[rules.required]"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" sm="6" md="6">
                    <v-select
                      prepend-icon="mdi-select-group"
                      label="Transaction Category*"
                      :disabled="!dialogValues.transaction_type"
                      v-model="dialogValues.category"
                      :items="transactionCategoryItems.filter(x => (is_adding || is_editing? !x.is_locked : x))"
                      item-text="title"
                      return-object
                      :rules="[rules.requiredObject]"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" sm="6" md="6">
                    <v-text-field
                      prepend-icon="mdi-currency-inr"
                      :rules="[rules.required, rules.amount]"
                      label="Amount*"
                      v-model="dialogValues.amount"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6" md="6" align-self="center">
                    <v-select
                      label="Account*"
                      v-model="dialogValues.account"
                      prepend-icon="mdi-bank"
                      item-value="id"
                      :items="allFinanceAccounts"
                      item-text="name"
                      :rules="[rules.required]"
                    ></v-select>
                  </v-col>
                  
                  <v-col cols="12" sm="6" md="6">
                    <v-menu
                      ref="menu2"
                      v-model="menu2"
                      :close-on-content-click="false"
                      :return-value.sync="dialogValues.transaction_date"
                      transition="scale-transition"
                      offset-y
                      max-width="290px"
                      min-width="290px"
                    >
                      <!-- Date-range picker -->
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          :value="formattedDate(dialogValues.transaction_date)"
                          label="Date*"
                          :rules="[rules.required]"
                          prepend-icon="mdi-calendar"
                          v-bind="attrs"
                          readonly
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="dialogValues.transaction_date"
                        no-title
                        scrollable
                        :readonly="!is_editing && !is_adding"
                      >
                        <v-spacer></v-spacer>
                        <v-btn text color="primary" @click="menu2 = false">
                          Cancel
                        </v-btn>
                        <v-btn
                          text
                          color="primary"
                          @click="$refs.menu2.save(dialogValues.transaction_date)"
                        >
                          OK
                        </v-btn>
                      </v-date-picker>
                    </v-menu>
                  </v-col>
                  <v-col
                    cols="12"
                    md="6"
                    sm="6"
                    v-if="dialogValues.transaction_type == 'I'"
                    align-self="center"
                  >
                    <v-text-field
                      prepend-icon="mdi-account-check"
                      v-model="dialogValues.payer"
                      label="Received From*"
                      :rules="[rules.required]"
                    >
                    </v-text-field>
                  </v-col>
                 
                  <v-col cols="12" md="8">
                    <v-select
                      prepend-icon="mdi-credit-card-outline"
                      label="Payment Mode*"
                      :rules="[rules.requiredObject]"
                      v-model="dialogValues.transaction_mode"
                      :items="paymentModes.map((x) => Object.assign({}, x))"
                      item-value="type"
                      return-object
                      item-text="title"
                    ></v-select>
                  </v-col>
                  <v-col
                    cols="12"
                    sm="6"
                    md="4"
                    align-self="center"
                    justify="center"
                  >
                    <v-btn
                      @click="viewDetails()"
                      :disabled="is_adding && !dialogValues.transaction_mode.title"
                    >
                      {{ this.is_adding ? "Add " : "View " }} Details
                    </v-btn>
                  </v-col>
                  <v-col cols="12">
                    <v-textarea
                      name="input-3-1"
                      prepend-icon="mdi-comment"
                      rows="1"
                      maxlength="250"
                      counter
                      label="Description*"
                      auto-grow
                      :rules="[rules.required]"
                      v-model="dialogValues.description"
                    ></v-textarea>
                  </v-col>
                  <v-col>
                    <v-file-input
                      accept="image/*, .pdf"
                      ref="fileAttachment"
                      label="Attachment"
                      show-size
                      :hint="dialogValues.attachment? dialogValues.attachment.split('/').pop(): ''"
                      :persistent-hint="dialogValues.attachment ? true : false"
                      @change="attachmentCallback($event)"
                      prepend-icon="mdi-paperclip"
                      :disabled="!is_adding && !is_editing"
                    >
                      <template v-slot:selection="{ text }">
                        <v-chip small label color="primary">
                          {{ text }}
                        </v-chip>
                      </template>
                    </v-file-input>
                  </v-col>
                  <v-col
                    v-if="!is_adding"
                    cols="12"
                    sm="4"
                    md="4"
                    align-self="center"
                  >
                    <a :href="dialogValues.attachment" target="_">
                      <v-btn :disabled="!dialogValues.attachment">
                        VIEW DOCUMENT
                      </v-btn>
                    </a>
                  </v-col>
                  <v-col
                    cols="12"
                    sm="1"
                    md="1"
                    align-self="center"
                    v-if="!is_adding && is_editing"
                  >
                    <v-icon
                      @click="removeAttachment()"
                      :disabled="!dialogValues.attachment"
                    >
                      mdi-delete
                    </v-icon>
                  </v-col>
                  <v-col cols="12">
                    <v-row>
                      <v-col
                        v-if="Object.keys(dialogValues.category).length"
                        cols="12"
                        sm="6"
                        md="6"
                        align-self="center"
                      >
                        <v-text-field
                          prepend-icon="mdi-receipt"
                          readonly
                          v-model="dialogValues.receipt_number"
                          label="Receipt Number*"
                        >
                        </v-text-field>
                      </v-col>
                      <v-col cols="12" sm="6" md="6" align-self="center">
                        <v-select
                          label="Manager"
                          hint="Person who handled the payment"
                          persistent-hint
                          v-model="dialogValues.manager"
                          prepend-icon="mdi-account-check"
                          item-value="id"
                          :items="filterManagerItems"
                          item-text="full_name"
                        ></v-select>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-container>
              <v-row
                class="justify-center pt-0 mx-2 py-4"
                v-if="!is_adding && !is_editing"
              >
                <!-- <v-spacer></v-spacer> -->
                <v-col cols="12" md="5" sm="6">
                  <v-btn block color="primary" class="pl-1" @click="goToFeeReceipt(dialogValues.linked_model_id)" v-if="dialogValues.category.code == 'FEES' ">
                    <v-icon class="px-2"> mdi-printer </v-icon>
                      Print Receipt
                  </v-btn>
                  <v-btn v-else block color="primary" class="pl-1" @click="printPdf(dialogValues.id)">
                    <v-icon class="px-2"> mdi-printer </v-icon>
                    Print Receipt
                  </v-btn>
                </v-col>
                <v-col cols="12" md="3" sm="6" v-if="currentUser.privileges.canEditFinanceIncomeTransactions">
                  <v-btn
                    block
                    color="indigo"
                    class="pl-1 white--text"
                    @click="editTransaction()"
                    :disabled="dialogValues.category.is_locked"
                  >
                    <v-icon color="white" class="px-2">
                      mdi-clipboard-edit-outline
                    </v-icon>
                    Edit
                  </v-btn>
                </v-col>
                <v-col cols="12" md="4" sm="6" v-if="currentUser.privileges.canDeleteFinanceIncomeTransactions">
                  <v-btn
                    block
                    color="red"
                    class="pl-1 white--text"
                    @click="showDeleteConfirmation = true"
                    :disabled="dialogValues.category.is_locked"
                  >
                    <v-icon color="white" class="px-2"> mdi-delete </v-icon>
                    Delete
                  </v-btn>
                </v-col>
              </v-row>
              <v-row
                class="justify-center pt-0 py-4"
                v-if="is_adding || is_editing"
              >
                <!-- <v-spacer></v-spacer> -->
                <v-col cols="12" md="5" sm="8">
                  <v-btn
                    block
                    color="indigo"
                    class="white--text"
                    @click="SaveChanges()"
                    v-model="submitForm"
                    :disabled="!submitForm"
                  >
                    Save Transaction
                  </v-btn>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-row>
    <v-row justify="center">
      <v-dialog v-model="dialog2" persistent max-width="630px">
        <v-card>
          <v-card-title class="justify-center">
            <span> {{ is_adding ? "Add" : "Edit" }} Payment Details</span>
            <v-btn icon absolute top right color="dark" @click="closeDialog2">
              x
            </v-btn>
          </v-card-title>
          <v-card-text class="">
            <v-form ref="form2" :readonly="!is_adding && !is_editing_details">
              <v-container class="mb-3">
                <v-row>
                  <v-col
                    cols="12"
                    sm="6"
                    md="6"
                    v-if="dialogValues.transaction_mode.type == 1"
                  >
                    <v-text-field
                      label="Cheque Number"
                      required
                      v-model="dialogValues.transaction_mode.cheque_number"
                    ></v-text-field>
                  </v-col>
                  <v-col
                    cols="12"
                    sm="6"
                    md="6"
                    v-if="dialogValues.transaction_mode.type == 1"
                  >
                    <v-text-field
                      label="Issued to"
                      required
                      v-model="dialogValues.transaction_mode.cheque_issued_to"
                    ></v-text-field>
                  </v-col>
                  <v-col
                    cols="12"
                    sm="6"
                    md="6"
                    v-if="dialogValues.transaction_mode.type == 2"
                  >
                    <v-text-field
                      label="Issued to"
                      required
                      v-model="dialogValues.transaction_mode.dd_issued_to"
                    ></v-text-field>
                  </v-col>
                  <v-col
                    cols="12"
                    md="6"
                    sm="6"
                    v-if="dialogValues.transaction_mode.type == 1 || dialogValues.transaction_mode.type == 2"
                  >
                    <v-menu
                      ref="menu3"
                      v-model="menu3"
                      :close-on-content-click="false"
                      :return-value.sync="paymentDate"
                      transition="scale-transition"
                      offset-y
                      max-width="290px"
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          :value="formattedDate(paymentDate)"
                          :label="dialogValues.transaction_mode.type == 1? 'Cheque Date' : 'DD Date'"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          class=""
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="paymentDate"
                        no-title
                        scrollable
                        :readonly="!is_editing_details && !is_adding"
                      >
                        <v-spacer></v-spacer>
                        <v-btn text color="primary" @click="menu3 = false">
                          Cancel
                        </v-btn>
                        <v-btn
                          text
                          color="primary"
                          @click="$refs.menu3.save(paymentDate)"
                        >
                          OK
                        </v-btn>
                      </v-date-picker>
                    </v-menu>
                  </v-col>
                  <v-col
                    cols="12"
                    sm="6"
                    md="6"
                    v-if="dialogValues.transaction_mode.type != 0"
                  >
                    <v-text-field
                      label="Bank Name"
                      required
                      v-model="dialogValues.transaction_mode.bank_name"
                    ></v-text-field>
                  </v-col>
                  <v-col
                    cols="12"
                    sm="6"
                    md="6"
                    v-if="dialogValues.transaction_mode.type == 1 || dialogValues.transaction_mode.type == 2"
                  >
                    <v-text-field
                      label="Bank Branch"
                      required
                      v-model="dialogValues.transaction_mode.bank_branch"
                    ></v-text-field>
                  </v-col>
                  <v-col
                    cols="12"
                    sm="6"
                    md="6"
                    v-if="dialogValues.transaction_mode.type == 3"
                  >
                    <v-text-field
                      label="UTR Number"
                      required
                      v-model="dialogValues.transaction_mode.utr_number"
                    ></v-text-field>
                  </v-col>
                  <v-col
                    cols="12"
                    sm="6"
                    md="6"
                    v-if="dialogValues.transaction_mode.type == 5 || dialogValues.transaction_mode.type == 6 || dialogValues.transaction_mode.type == 4"
                  >
                    <v-text-field
                      label="Transaction ID"
                      required
                      v-model="dialogValues.transaction_mode.transaction_id"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-textarea
                      name="input-3-1"
                      prepend-icon="mdi-comment"
                      rows="1"
                      maxlength="250"
                      counter
                      label="Remarks"
                      auto-grow
                      v-model="dialogValues.transaction_mode.remarks"
                    ></v-textarea>
                  </v-col>
                </v-row>
                <v-row
                  class="justify-center pt-0 py-4"
                  v-if="!is_adding && !is_editing_details"
                >
                  <v-col cols="12" md="5" sm="8">
                    <v-btn
                      block
                      color="indigo"
                      class="white--text"
                      @click="editDetails()"
                      :disabled="dialogValues.category.is_locked"
                      v-if="is_editing || is_adding"
                    >
                      Edit
                    </v-btn>
                  </v-col>
                </v-row>
                <v-row
                  class="justify-center pt-0 py-4"
                  v-if="is_editing_details || is_adding"
                >
                  <v-col cols="12" md="5" sm="8">
                    <v-btn
                      block
                      color="indigo"
                      class="white--text"
                      @click="closeDialog2"
                      v-if="is_editing || is_adding"
                    >
                      Save
                    </v-btn>
                  </v-col>
                </v-row>
              </v-container>
            </v-form>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-row>
    <v-row justify="center">
      <DeleteConfirmationDialog
        title="Are you sure you want to delete this transaction?"
        :visible="showDeleteConfirmation"
        @deleteObject="deleteRecord"
      />
    </v-row>
    <!-- snackbar -->
    <v-snackbar v-model="snackbar.visible" :color="snackbar.color" multi-line>
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn dark text v-bind="attrs" @click="snackbar.visible = false"
          >Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import moment from "moment";
import api from "@api/index";
import { mapActions, mapGetters } from "vuex";
import endpoints from "@api/repository";
import Helper from "@utils/misc";
import JsonExcel from "vue-json-excel";
import DeleteConfirmationDialog from '@components/widgets/dialogs/DeleteConfirmationDialog.vue'

export default {
  name: "OldIncomes",

  components: {
    downloadExcel: JsonExcel,
    DeleteConfirmationDialog
  },

  data() {
    return {
      rules: {
        required: (value) => !!value || "Required",
        requiredObject: (value) => Object.keys(value).length > 0 || "Required",
        amount: (v) => /^[0-9]+(\.\d{1,2})?%?$/.test(v) || "Invalid",
      },
      submitForm: false,
      showDeleteConfirmation: false,
      moment: moment,
      search: "",
      menu: false,
      dateRange: null,
      dialog: false,
      dialog2: false,
      is_editing: false,
      is_adding: false,
      is_editing_details: false,
      menu2: false,
      menu3: false,
      paymentDate: null,
      options: {},
      pagination: {
        totalCount: 0,
        url: endpoints.financeTransactions,
      },

      snackbar: {
        visible: false,
        message: "",
        color: "dark",
      },

      dialogValues: {
        category: {},
        transaction_mode: {},
      },

      attachmentFileString: null,

      transactionHeaders: [
    {
          text: "Description",
          value: "description",
          align: "start",
          sortable: false,
        },
        {
          text: "Category",
          value: "category",
          sortable: true,
          align: "center",
        },
        
        {
          text: "Receipt No.",
          value: "receipt_number",
          sortable: false,
          align: "center",
        },
        {
          text: "Received From",
          value: "payer",
          align: "center",
          sortable: false,
        },
        {
          text: "Date",
          value: "transaction_date",
          align: "center",
          sortable: true,
        },
        {
          text: "Account",
          value: "account",
          sortable: true,
          align: "center",
        },
        {
          text: "Amount",
          value: "amount",
          sortable: true,
        },
      ],

      transactionItems: [],
      allFinanceAccounts: [],

      paymentModes: [
        {
          type: 0,
          title: "Cash",
        },
        {
          type: 1,
          title: "Cheque",
        },
        {
          type: 2,
          title: "DD",
        },
        {
          type: 3,
          title: "NEFT/RTGS",
        },
        {
          type: 4,
          title: "Online/Net Banking",
        },
        {
          type: 5,
          title: "Debit Card",
        },
        {
          type: 6,
          title: "Credit Card",
        },
      ],

      transactionTypeItems: [
        {
          title: "Income",
          transaction_type: "I",
        },
      ],

      transactionCategoryItems: [],

      filter: {
        accounts: null,
        mode: null,
        manager: null,
        category: null,
      },

      filterManagerItems: [],
      export_in_progress: false,
    };
  },

  computed: {
    ...mapGetters([
      "currentUser",
      "accessToken",
      "isLoading",
      "currentInstitution",
    ]),
    essentials() {
      return {
        accessToken: this.accessToken,
        setLoading: this.setLoading,
        handleErrorsFunction: this.handleApiErrors,
      };
    },

    getExportFields() {
      return {
        Date: {
          field: "transaction_date",
          callback: (value) => {
            return this.moment(value).format("D MMM, YYYY");
          },
        },
        Category: "category.title",
        Amount: "amount",
        Account: "account.name",
        "Payment Mode": {
          field: "transaction_mode.type",
          callback: (value) => {
            return this.paymentModes[value].title;
          },
        },
        "Received From": {
          field: 'payer',
          callback: (value)=> {
            return value || '-';
          }
        },
        "Created by": "creator.full_name",
        "Created on": {
          field: "created",
          callback: (value) => {
            return this.moment(value).format("D MMM, YYYY");
          },
        },
        "Updated by": {
          field: 'updator.full_name',
          callback: (value)=>{
            return value || '-';
          }
        },
        "Updated on": {
          field: "updated",
          callback: (value) => {
            return this.moment(value).format("D MMM, YYYY");
          }
        },
        "Manager": "manager.full_name",
        "Receipt Number": "receipt_number",
        "Description": "description",
      };
    },
  },

  methods: {
    ...mapActions(["setLoading"]),

    // handle api errors
    async handleApiErrors(err) {
      this.setLoading(false);
      console.log(err);
      if (err.response) {
        this.snackbar.message = "Unable to process request";
        this.snackbar.color = "error";
        this.snackbar.visible = true;
      }
    },

    async printPdf(transaction_id) {
      var windowOptions =
          "directories=no,titlebar=no,toolbar=no,location=no,status=no,menubar=no,scrollbars=no,resizable=no,width=1024,height=768";
      window.open(
          "/finance/transactions/print/" +
          transaction_id,
          "Print Payslip",
          windowOptions
      );
    },

    async goToFeeReceipt(receiptId) {
       var windowOptions =
        "directories=no,titlebar=no,toolbar=no,location=no,status=no,menubar=no,scrollbars=no,resizable=no,width=1024,height=768";
      window.open(
        "/fees/records/students/receipt/print/" +
          receiptId +
          "/" +
          (this.printTwoCopies ? "2" : "1"),
        "Print Receipt",
        windowOptions
      );      
    },

    formattedDateRange(dateRange) {
      if (!dateRange) return;

      var formatted = moment(dateRange[0]).format("D MMM, YYYY") + " - " + moment(dateRange[1]).format("D MMM, YYYY");
      return formatted;
    },

    formattedDate(date) {
      if (!date) return;
      var formatted = moment(date).format("D MMM, YYYY");
      return formatted;
    },

    viewDetails() {
      this.dialog2 = true;
    },

    editDetails() {
      this.is_editing_details = true;
    },

    async getReceiptNumber() {
      var url = endpoints.incomeReceipt + `${this.currentInstitution.id}/`;
      var response = await api.call(this.essentials, url);
      if (response) {
        this.dialogValues.receipt_number = response.next_receipt_number;
      }
    },

    editTransaction() {
      this.is_editing = true;
    },

    openAddDialog() {
      this.is_adding = true;
      this.dialog = true;
      this.dialogValues.attachment = null;
      this.dialogValues.transaction_type = 'I';
      this.getReceiptNumber();
    },

    closeDialog() {
      this.dialog = false;
      var self = this;
      setTimeout(() => {
        self.attachmentFileString = null;
        self.is_adding = false;
        self.is_editing = false;
        self.is_editing_details = false;
        self.paymentDate = null;
        this.dialogValues.transaction_mode = null;
        self.$refs.form.reset();
        self.dialogValues = {
          category: {},
          transaction_mode: {},
        };
      }, 400);
    },

    closeDialog2() {
      this.dialog2 = false;
    },

    removeAttachment() {
      this.dialogValues.attachment = null;
      this.attachmentFileString = null;
    },

    async attachmentCallback(payload) {
      const file = payload;
      if (file) {
        this.attachmentFileString = await Helper.toBase64(file);
      } else {
        this.attachmentFileString = null;
      }
    },

    async handleClick(value) {
      var url = endpoints.financeTransactions + `${value.id}/`;

      var response = await api.call(this.essentials, url);

      if (response) {
        this.dialogValues = response;
        if (response.transaction_mode.type == 2)
          this.paymentDate = this.dialogValues.transaction_mode.dd_date;
        else if (response.transaction_mode.type == 1)
          this.paymentDate = this.dialogValues.transaction_mode.cheque_date;
        this.dialog = true;
      }
      
    },

    async SaveChanges() {
      var url = endpoints.financeTransactions;
      var response;
      if (!this.dialogValues.institution)
        this.dialogValues.institution = this.dialogValues.category.institution;

      if (this.attachmentFileString)
        this.dialogValues.attachment = this.attachmentFileString;

      delete this.dialogValues["invoice_number"];

      if (this.is_adding) {
        if (this.dialogValues.transaction_mode.type == 1 && this.paymentDate) {
          this.dialogValues.transaction_mode.cheque_date = this.paymentDate;
        } else if (
          this.dialogValues.transaction_mode.type == 2 &&
          this.paymentDate
        ) {
          this.dialogValues.transaction_mode.dd_date = this.paymentDate;
        }
        response = await api.call(
          this.essentials,
          url,
          api.Methods.POST,
          this.dialogValues
        );

        if (response) {
          this.snackbar.color="dark"
          this.snackbar.message = "Transaction added successfully";
          this.snackbar.visible = true;
        }
      } else if (this.is_editing) {
        url += `${this.dialogValues.id}/`;
        response = await api.call(
          this.essentials,
          url,
          api.Methods.PUT,
          this.dialogValues
        );

        if (response) {
          this.snackbar.color="dark"
          this.snackbar.message = "Transaction edited successfully";
          this.snackbar.visible = true;
        }
      }

      this.attachmentFileString = null;
      this.is_adding = false;
      this.is_editing = false;
      this.is_editing_details = false;
      this.paymentDate = null;
      if(this.dialogValues.attachment)
      this.$refs.fileAttachment.reset()
      this.getTransactions();
      this.handleClick(response);
    },

    async deleteRecord(confirm) {
      this.showDeleteConfirmation = false;
      var url = endpoints.financeTransactions;
      var response;
      if(confirm) {
        url += `${this.dialogValues.id}/`;
        response = await api.call(this.essentials, url, api.Methods.DELETE);
        if (response) {
          this.snackbar.color="dark"
          this.snackbar.message = "Record deleted successfully";
          this.snackbar.visible = true;
          this.getTransactions();
          this.closeDialog();
        }
      }
    },

    async getStaff() {
      var url = endpoints.getAllStaff + `?all=true`;
      url = Helper.addUrlParams(url, "all=true");
      var response = await api.call(this.essentials, url);
      if (response) {
        this.filterManagerItems = response;
      }
    },

    async getCategories() {
      var url = endpoints.financeCategories;
      url = Helper.addUrlParams(url,`transaction_type=I`)
      var response = await api.call(this.essentials, url);
      if (response) {
        this.transactionCategoryItems = response;
      }
    },

    async getTransactions() {
      var url = this.pagination.url;

      url = Helper.addUrlParams(url,`transaction_type__in=I`)

      if (this.search) {
        url = Helper.addUrlParams(url, "search=" + this.search);
      }

      if (this.filter.category) {
        url = Helper.addUrlParams(url,"category__in=" + this.filter.category.toString());
      }

      if (this.dateRange) {
        url = Helper.addUrlParams(url,`transaction_date__gte=${this.dateRange[0].toString()}&transaction_date__lte=${this.dateRange[1].toString()}`);
      }

      if (this.filter.manager) {
        url = Helper.addUrlParams(url,"manager__in=" + this.filter.manager.toString());
      }

      if (this.filter.mode) {
        url = Helper.addUrlParams(url,"transaction_mode__type__in=" + this.filter.mode.toString());
      }
      if (this.filter.accounts) {
        url = Helper.addUrlParams(
          url,
          "account__in=" + this.filter.accounts.toString()
        );
      }

      var response = await api.call(this.essentials, url);
      if (response) {
        this.transactionItems = response.results;
        this.pagination.totalCount = response.count;
        this.pagination.nextUrl = response.next;
      }
    },

    async getExportData() {
      this.export_in_progress = true;
      var url = endpoints.financeExport;
      url = this.getParamsURL(Helper.addUrlParams(url,`transaction_type__in=I`))

      if (this.search) {
        url = Helper.addUrlParams(url, "search=" + this.search);
      }

      if (this.filter.category) {
        url = Helper.addUrlParams(url,"category__in=" + this.filter.category.toString());
      }

      if (this.dateRange) {
        url = Helper.addUrlParams( url,`transaction_date__gte=${this.dateRange[0].toString()}&transaction_date__lte=${this.dateRange[1].toString()}`);
      }

      if (this.filter.manager) {
        url = Helper.addUrlParams(url,"creator__in=" + this.filter.manager.toString());
      }

      if (this.filter.mode) {
        url = Helper.addUrlParams(url,"transaction_mode__type__in=" + this.filter.mode.toString());
      }

      var response = await api.call(this.essentials, url);
      if (response) {
        this.export_in_progress = false;
        return response;
      }
    },

    async getAllFinanceAccounts() {
      this.allFinanceAccounts = await api.call(this.essentials, endpoints.financeAccounts);
    },

    getParamsURL(url){
      url = Helper.addUrlParams(url, "page=" + this.options.page)
        if (this.options.sortBy.length > 0) {
          var params = "&ordering=";
          for (var sortOption in this.options.sortBy) {
            params +=
              (this.options.sortDesc[sortOption] ? "" : "-") +
              this.options.sortBy[sortOption];
            if (sortOption != this.options.sortBy.length - 1) params += ",";
          }
          params = Helper.replaceTextFromArray(params, [
            ["transaction_mode", "transaction_mode__type"],
            ["creator", "creator__full_name"],
            ["category", "category__title"],
          ]);

          url += params;
        }
        return url;
    },
  },

  watch: {
    options: {
      async handler() {
        this.pagination.url = this.getParamsURL(endpoints.financeTransactions);
        this.getTransactions();
      },
    },
    search: {
      async handler() {
        this.options.page = 1;
        var searched = this.search;
        var self = this;
        setTimeout(function () {
          if (searched == self.search) {
            self.getTransactions();
          }
        }, 500);
      },
    },
  },

  async created() {
    this.getAllFinanceAccounts();
    this.getCategories();
    this.getStaff();

  },
};
</script>

<style lang="css" scoped>
a {
  text-decoration: none;
}
html {
  overflow: auto !important;
}

.row-pointer >>> tbody tr :hover {
  cursor: pointer;
}
</style>

<!-- 
*) export sorted data (currently random)
-->
